// Translation dictionaries for the SPA. English ('en') is the source of truth:
// its keys define the valid `TranslationKey` union, and the Spanish ('es')
// dictionary must supply the same keys. Messages may contain `{param}`
// placeholders that `t(key, params)` interpolates at render time (e.g.
// "Between {min} and {max} characters.").
//
// Keys are namespaced by screen/area (e.g. `projects.title`, `auth.signOut`) to
// avoid collisions. When adding a user-facing string, add it here in BOTH
// languages and reference it via `useT()`'s `t(...)`.

export type Language = 'en' | 'es';

// The English dictionary is the canonical shape. Every key here becomes a valid
// `TranslationKey`, and `es` below is checked against this same shape.
export const en = {
  // App-wide / layout
  'app.title': 'AI Course Creator',
  'app.loading': 'Loading…',
  'auth.signOut': 'Sign out',
  'language.label': 'Language',
  'language.en': 'EN',
  'language.es': 'ES',

  // Sign-in screen
  'auth.subtitle.signIn': 'Sign in to continue',
  'auth.subtitle.signUp': 'Create your account',
  'auth.subtitle.confirm': 'Confirm your account',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.confirmationCode': 'Confirmation code',
  'auth.genericError': 'An error occurred.',
  'auth.processing': 'Processing…',
  'auth.signIn': 'Sign in',
  'auth.signUp': 'Sign up',
  'auth.confirm': 'Confirm',
  'auth.noAccount': "Don't have an account? Sign up",
  'auth.backToSignIn': 'Back to sign in',

  // Roles
  'role.owner': 'Owner',
  'role.editor': 'Editor',
  'role.viewer': 'Viewer',
  'role.member': 'Member',

  // Projects list
  'projects.title': 'Projects',
  'projects.loading': 'Loading projects…',
  'projects.loadError': 'Could not load projects: {message}',
  'projects.retry': 'Retry',
  'projects.create': 'Create project',
  'projects.emptyTitle': "You don't have any projects yet.",
  'projects.emptyHint': 'Create your first project to get started.',
  'projects.noDescription': 'No description',
  'projects.form.title': 'Create project',
  'projects.form.name': 'Name',
  'projects.form.nameHint': 'Between {min} and {max} characters.',
  'projects.form.description': 'Description (optional)',
  'projects.form.nameInvalid':
    'The project name must be between {min} and {max} characters and cannot be empty.',
  'projects.form.createError': 'Could not create the project.',
  'projects.form.cancel': 'Cancel',
  'projects.form.creating': 'Creating…',
  'projects.form.create': 'Create',

  // Project detail
  'project.backToProjects': '← Projects',
  'project.notSpecified': 'Project not specified.',
  'project.fallbackTitle': 'Project',
  'project.loadError': 'Could not load the project: {message}',
  'project.sectionsNav': 'Project sections',
  'project.tab.courses': 'Courses',
  'project.tab.drafts': 'Drafts',
  'project.tab.voiceProfiles': 'Voice profiles',
  'project.tab.artStyles': 'Art styles',
  'project.tab.generations': 'Generations',
  'project.tab.collaborators': 'Collaborators',

  // Course statuses
  'courseStatus.draft': 'Draft',
  'courseStatus.proposed': 'Proposed',
  'courseStatus.approved': 'Approved',
  'courseStatus.generating': 'Generating',
  'courseStatus.ready': 'Ready',

  // Episode generation statuses
  'episodeStatus.none': 'Not generated',
  'episodeStatus.pending': 'Pending',
  'episodeStatus.running': 'In progress',
  'episodeStatus.ready': 'Ready',
  'episodeStatus.failed': 'Failed',

  // Course detail
  'course.notSpecified': 'Course not specified.',
  'course.backToProject': '← Back to project',
  'course.loading': 'Loading course…',
  'course.loadError': 'Could not load the course: {message}',
  'course.notFound': 'The requested course was not found.',
  'course.language': 'Language: {language}',
  'course.episodes': 'Episodes',
  'course.noEpisodes': 'This course has no episodes yet.',
  'course.episodeIndex': 'Episode {index}',
  'course.field.script': 'Script',
  'course.field.scriptAvailable': 'Available',
  'course.viewScript': 'View script',
  'course.field.scriptMissing': 'No script',
  'course.field.proposedDuration': 'Proposed duration',
  'course.field.durationSeconds': '{seconds} s',
  'course.field.durationUndefined': 'Not defined',
  'course.field.audio': 'Audio',
  'course.field.video': 'Video',
  'course.generateAudio': 'Generate audio',
  'course.generateVideo': 'Generate video',
  'course.starting': 'Starting…',
  'course.audioStarted': 'Audio generation started. Track its status on the Generations tab.',
  'course.videoStarted': 'Video generation started. Track its status on the Generations tab.',
  'course.audioStartError': 'Could not start audio generation.',
  'course.videoStartError': 'Could not start video generation.',
  'course.generationQueued': '{kind} generation queued…',
  'course.generationRunning': 'Generating {kind}…',
  'course.generationReady': '{kind} ready — the player will appear below.',
  'course.generationFailed': '{kind} generation failed: {message}',
  'course.generationFailedNoDetail': 'please try again',
  'course.playAudio': 'Play audio',
  'course.playVideo': 'Play video',
  'course.mediaLoading': 'Loading…',
  'course.mediaUrlError': 'Could not get the playback link.',
  'course.audioUnsupported': 'Your browser does not support audio playback.',
  'course.videoUnsupported': 'Your browser does not support video playback.',

  // Agent generate section
  'agent.title': 'Generate a course with the agent',
  'agent.close': 'Close',
  'agent.open': 'Research and propose a structure',
  'agent.lead':
    'The agent will research the topic and propose a {min}–{max} episode structure that you can review and edit before approving it.',
  'agent.field.title': 'Title',
  'agent.field.titlePlaceholder': 'Course for managing a panic attack',
  'agent.field.titleHint': 'Between {min} and {max} characters.',
  'agent.field.topic': 'Course topic',
  'agent.field.topicPlaceholder': 'Make a course in Spanish that helps during a panic attack.',
  'agent.field.topicHint': 'Between {min} and {max} characters.',
  'agent.field.focusAreas': 'Focus areas (optional)',
  'agent.field.focusAreasPlaceholder': 'Breathing, cognitive reframing, grounding techniques…',
  'agent.field.episodes': 'Episodes ({min}–{max})',
  'agent.field.voiceProfile': 'Voice profile',
  'agent.field.artStyle': 'Art style',
  'agent.noVoiceProfiles': 'No voice profiles',
  'agent.noArtStyles': 'No art styles',
  'agent.defaultSuffix': ' (default)',
  'agent.focusAreasPrefix': 'Focus areas',
  'agent.titleInvalid':
    'The course title must be between {min} and {max} characters and cannot be empty.',
  'agent.topicInvalid':
    'The course topic must be between {min} and {max} characters and cannot be empty.',
  'agent.episodeCountInvalid': 'The number of episodes must be between {min} and {max}.',
  'agent.startError': 'Could not start generation with the agent.',
  'agent.submitting': 'Submitting…',
  'agent.submit': 'Research and propose',
  'agent.generating':
    'The agent is researching the topic and preparing a structure proposal. This may take a few minutes.',
  'agent.generatingFollow': 'You can also track the status on the {link} tab.',
  'agent.generationsLink': 'Drafts',
  'agent.pollTimeout':
    'The proposal is taking longer than usual. You can follow its status on the Drafts tab.',
  'agent.generationFailed': 'The proposal generation failed. Please try again.',
  'agent.cancel': 'Cancel',
  'agent.proposedStructure': 'Proposed structure',
  'agent.overviewLabel': "Agent's plan",
  'agent.reviewHint':
    'Review and edit the episodes. The course must have between {min} and {max} episodes.',
  'agent.episodeTitlePlaceholder': 'Episode title',
  'agent.episodeTitleLabel': 'Title of episode {index}',
  'agent.episodeDurationLabel': 'Duration of episode {index} in seconds',
  'agent.removeEpisode': 'Remove',
  'agent.removeEpisodeLabel': 'Remove episode {index}',
  'agent.addEpisode': 'Add episode',
  'agent.episodeCount': '{count} of {max} episodes',
  'agent.regenerateError': 'Could not request a new proposal.',
  'agent.episodeCountRangeError': 'The course must have between {min} and {max} episodes.',
  'agent.episodeTitleRequired': 'Each episode needs a title.',
  'agent.episodeDurationRequired': 'Each episode needs a duration in seconds greater than zero.',
  'agent.approveError': 'Could not approve the structure.',
  'agent.approving': 'Approving…',
  'agent.approve': 'Approve and generate scripts',
  'agent.requesting': 'Requesting…',
  'agent.reject': 'Reject and regenerate',
  'agent.discard': 'Discard',
  'agent.approved':
    'Structure approved. The scripts are being generated; follow the progress on the Generations tab.',
  'agent.generateAnother': 'Generate another course',

  // Drafts tab — in-progress AI course generations (proposing, awaiting review,
  // generating, or failed) and the draft detail view.
  'drafts.title': 'Drafts',
  'drafts.refresh': 'Refresh',
  'drafts.loading': 'Loading drafts…',
  'drafts.loadError': 'Could not load the drafts: {message}',
  'drafts.empty':
    'No drafts yet. Start an AI course generation from the Courses tab to create one.',
  'drafts.stage.proposing': 'Proposing',
  'drafts.stage.awaitingReview': 'Awaiting review',
  'drafts.stage.generating': 'Generating',
  'drafts.stage.failed': 'Failed',
  'drafts.detail.title': 'Course draft',
  'drafts.detail.back': '← Back to drafts',
  'drafts.detail.notSpecified': 'No draft was specified.',
  'drafts.detail.loading': 'Loading the draft…',
  'drafts.detail.loadingCourse': 'Loading the course…',
  'drafts.detail.notFound': 'This draft is no longer available.',
  'drafts.detail.loadError': 'Could not load the draft: {message}',
  'drafts.detail.failedNoDescription': 'The generation failed.',
  'drafts.detail.proposing':
    'The agent is researching the topic and proposing a course structure. This view updates automatically.',
  'drafts.detail.generating':
    'The course was approved. Scripts and media are being generated; this view updates automatically.',
  'drafts.detail.generated': 'All generation is complete.',
  'drafts.detail.script': 'Script',
  'drafts.detail.scriptReady': 'ready',
  'drafts.detail.scriptPending': 'pending',
  'drafts.detail.audio': 'Audio',
  'drafts.detail.video': 'Video',
  'drafts.detail.openCourse': 'Open course',
  'drafts.detail.scriptsGenerating':
    'The agent is writing a narration script for each episode. When it finishes, the course and its episodes are created and appear in the Courses tab.',
  'drafts.detail.stepScripts': 'Writing one narration script per episode…',
  'drafts.detail.stepCourse': 'Creating the course and its episodes',
  'drafts.detail.stepMedia': 'Audio and video are generated later, per episode, from the course',
  'drafts.detail.autoRefresh': 'This view updates automatically.',
  'drafts.detail.whatGenerated':
    'Each episode has a generated narration script. Generate audio and video per episode from the course.',

  // Courses tab
  'courses.title': 'Courses',
  'courses.loading': 'Loading courses…',
  'courses.loadError': 'Could not load the courses: {message}',
  'courses.retry': 'Retry',
  'courses.createManually': 'Create course manually',
  'courses.close': 'Close',
  'courses.empty': 'This project has no courses yet.',
  'courses.form.title': 'Title',
  'courses.form.titlePlaceholder': 'Introduction to meditation',
  'courses.form.titleHint': 'Between {min} and {max} characters.',
  'courses.form.topic': 'Course topic',
  'courses.form.topicPlaceholder': 'Describe the topic and focus of the course',
  'courses.form.topicHint': 'Between {min} and {max} characters.',
  'courses.form.titleInvalid':
    'The course title must be between {min} and {max} characters and cannot be empty.',
  'courses.form.topicInvalid':
    'The course topic must be between {min} and {max} characters and cannot be empty.',
  'courses.form.createError': 'Could not create the course.',
  'courses.form.creating': 'Creating…',
  'courses.form.create': 'Create course',

  // Collaborators tab
  'collaborators.title': 'Collaborators',
  'collaborators.loading': 'Loading collaborators…',
  'collaborators.loadError': 'Could not load the collaborators: {message}',
  'collaborators.retry': 'Retry',
  'collaborators.empty': 'This project has no collaborators yet.',
  'collaborators.invite.email': 'Email',
  'collaborators.invite.emailPlaceholder': 'person@example.com',
  'collaborators.invite.role': 'Role',
  'collaborators.invite.emailRequired': 'Enter the email of the person you want to invite.',
  'collaborators.invite.error': 'Could not invite the collaborator.',
  'collaborators.invite.inviting': 'Inviting…',
  'collaborators.invite.invite': 'Invite',
  'collaborators.role.of': 'Role of {email}',
  'collaborators.roleChangeError': 'Could not change the role.',
  'collaborators.removeError': 'Could not remove the collaborator.',
  'collaborators.removing': 'Removing…',
  'collaborators.remove': 'Remove',
  'collaborators.soleOwnerTitle': 'The sole owner cannot be removed',
  'collaborators.soleOwnerHint':
    'Sole owner: their role cannot be changed and they cannot be removed.',

  // Voice profiles tab
  'voiceProfiles.title': 'Voice profiles',
  'voiceProfiles.loading': 'Loading voice profiles…',
  'voiceProfiles.loadError': 'Could not load the voice profiles: {message}',
  'voiceProfiles.retry': 'Retry',
  'voiceProfiles.empty': 'This project has no voice profiles yet.',
  'voiceProfiles.default': 'Default',
  'voiceProfiles.field.voice': 'Voice',
  'voiceProfiles.field.language': 'Language',
  'voiceProfiles.form.name': 'Name',
  'voiceProfiles.form.namePlaceholder': 'Narrator voice',
  'voiceProfiles.form.voiceId': 'Voice ID',
  'voiceProfiles.form.voiceIdPlaceholder': 'ElevenLabs voiceId',
  'voiceProfiles.form.language': 'Language',
  'voiceProfiles.form.languagePlaceholder': 'es',
  'voiceProfiles.form.voiceSettings': 'Voice settings (optional JSON)',
  'voiceProfiles.form.pausePattern': 'Pause pattern (optional JSON)',
  'voiceProfiles.form.default': 'Set as default voice profile',
  'voiceProfiles.form.limitReached': 'The maximum of {limit} voice profiles has been reached.',
  'voiceProfiles.form.limitHint':
    'The maximum of {limit} voice profiles for this project has been reached.',
  'voiceProfiles.form.requiredFields': 'Name, voice ID, and language are required.',
  'voiceProfiles.form.voiceSettingsLabel': 'The voice settings',
  'voiceProfiles.form.pausePatternLabel': 'The pause pattern',
  'voiceProfiles.form.jsonInvalid': '{field} must be valid JSON.',
  'voiceProfiles.form.jsonNotObject': '{field} must be a JSON object.',
  'voiceProfiles.form.createError': 'Could not create the voice profile.',
  'voiceProfiles.form.creating': 'Creating…',
  'voiceProfiles.form.create': 'Create voice profile',

  // Art styles tab
  'artStyles.title': 'Art styles',
  'artStyles.loading': 'Loading art styles…',
  'artStyles.loadError': 'Could not load the art styles: {message}',
  'artStyles.retry': 'Retry',
  'artStyles.empty': 'This project has no art styles yet.',
  'artStyles.default': 'Default',
  'artStyles.field.style': 'Style',
  'artStyles.field.palette': 'Palette',
  'artStyles.field.aspectRatio': 'Aspect ratio',
  'artStyles.field.resolution': 'Resolution',
  'artStyles.form.name': 'Name',
  'artStyles.form.namePlaceholder': 'Soft watercolor',
  'artStyles.form.style': 'Style',
  'artStyles.form.stylePlaceholder': 'watercolor',
  'artStyles.form.aspectRatio': 'Aspect ratio (optional)',
  'artStyles.form.resolution': 'Resolution (optional)',
  'artStyles.form.palette': 'Color palette',
  'artStyles.form.paletteColorLabel': 'Pick a color',
  'artStyles.form.paletteAdd': 'Add color',
  'artStyles.form.paletteRemove': 'Remove {color}',
  'artStyles.form.veoHints': 'Veo prompt hints (optional)',
  'artStyles.form.negativePrompt': 'Negative prompt (optional)',
  'artStyles.form.referenceImages': 'Reference images (optional)',
  'artStyles.form.uploading': 'Uploading…',
  'artStyles.form.uploadError': 'Could not upload the image. Please try again.',
  'artStyles.form.referenceRemove': 'Remove reference image',
  'artStyles.form.default': 'Set as default art style',
  'artStyles.form.limitReached': 'The maximum of {limit} art styles has been reached.',
  'artStyles.form.limitHint':
    'The maximum of {limit} art styles for this project has been reached.',
  'artStyles.form.requiredFields': 'Name and style are required.',
  'artStyles.form.paletteRequired': 'Add at least one color to the palette.',
  'artStyles.form.createError': 'Could not create the art style.',
  'artStyles.form.creating': 'Creating…',
  'artStyles.form.create': 'Create art style',

  // Generation statuses
  'generationStatus.PENDING': 'Pending',
  'generationStatus.RUNNING': 'In progress',
  'generationStatus.READY': 'Ready',
  'generationStatus.FAILED': 'Failed',

  // Generation kinds
  'generationKind.AGENT_COURSE': 'Course structure',
  'generationKind.EPISODE_AUDIO': 'Episode audio',
  'generationKind.EPISODE_VIDEO': 'Episode video',

  // Generations tab
  'generations.title': 'Generations',
  'generations.loading': 'Loading generations…',
  'generations.loadError': 'Could not load generations: {message}',
  'generations.refresh': 'Refresh',
  'generations.subscribeError': 'Could not receive generation updates: {message}',
  'generations.empty':
    'No active generations. The status of each job will appear here in real time as soon as it starts.',
  'generations.episode': ' · Episode {index}',
  'generations.failedNoDescription': 'The generation failed without an error description.',
  // API keys
  'apiKeys.title': 'API keys',
  'apiKeys.subtitle':
    'Generate keys to call the API programmatically as your user, with per-key permissions.',
  'apiKeys.create': 'Create key',
  'apiKeys.creating': 'Creating…',
  'apiKeys.loading': 'Loading API keys…',
  'apiKeys.retry': 'Retry',
  'apiKeys.cancel': 'Cancel',
  'apiKeys.loadError': 'Could not load API keys: {message}',
  'apiKeys.createError': 'Could not create the API key.',
  'apiKeys.empty': 'You have no API keys yet.',
  'apiKeys.name': 'Name',
  'apiKeys.nameRequired': 'A name is required.',
  'apiKeys.scopesLabel': 'Scopes',
  'apiKeys.scopeRequired': 'Select at least one scope.',
  'apiKeys.projectsField': 'Restrict to projects (optional)',
  'apiKeys.projectsPlaceholder': 'project-id-1, project-id-2',
  'apiKeys.projectsHint': 'Comma-separated project ids. Leave empty to allow all of your projects.',
  'apiKeys.projectsLabel': 'Projects',
  'apiKeys.allProjects': 'All projects',
  'apiKeys.expiresField': 'Expires (optional)',
  'apiKeys.createdLabel': 'Created',
  'apiKeys.expiresLabel': 'Expires',
  'apiKeys.lastUsedLabel': 'Last used',
  'apiKeys.revoke': 'Revoke',
  'apiKeys.revoked': 'Revoked',
  'apiKeys.none': 'None',
  'apiKeys.revealWarning': "Copy this key now. You won't be able to see it again.",
  'apiKeys.copy': 'Copy',
  'apiKeys.dismiss': 'Dismiss',
  'nav.apiKeys': 'API keys',
} as const;

// Valid keys are exactly the keys of the English dictionary.
export type TranslationKey = keyof typeof en;

// The Spanish dictionary must supply every key. Typing it as
// `Record<TranslationKey, string>` makes a missing/extra key a compile error.
export const es: Record<TranslationKey, string> = {
  // App-wide / layout
  'app.title': 'AI Course Creator',
  'app.loading': 'Cargando…',
  'auth.signOut': 'Cerrar sesión',
  'language.label': 'Idioma',
  'language.en': 'EN',
  'language.es': 'ES',

  // Sign-in screen
  'auth.subtitle.signIn': 'Inicia sesión para continuar',
  'auth.subtitle.signUp': 'Crea tu cuenta',
  'auth.subtitle.confirm': 'Confirma tu cuenta',
  'auth.email': 'Correo electrónico',
  'auth.password': 'Contraseña',
  'auth.confirmationCode': 'Código de confirmación',
  'auth.genericError': 'Se produjo un error.',
  'auth.processing': 'Procesando…',
  'auth.signIn': 'Iniciar sesión',
  'auth.signUp': 'Registrarse',
  'auth.confirm': 'Confirmar',
  'auth.noAccount': '¿No tienes cuenta? Regístrate',
  'auth.backToSignIn': 'Volver a iniciar sesión',

  // Roles
  'role.owner': 'Propietario',
  'role.editor': 'Editor',
  'role.viewer': 'Lector',
  'role.member': 'Miembro',

  // Projects list
  'projects.title': 'Proyectos',
  'projects.loading': 'Cargando proyectos…',
  'projects.loadError': 'No se pudieron cargar los proyectos: {message}',
  'projects.retry': 'Reintentar',
  'projects.create': 'Crear proyecto',
  'projects.emptyTitle': 'Aún no tienes proyectos.',
  'projects.emptyHint': 'Crea tu primer proyecto para empezar.',
  'projects.noDescription': 'Sin descripción',
  'projects.form.title': 'Crear proyecto',
  'projects.form.name': 'Nombre',
  'projects.form.nameHint': 'Entre {min} y {max} caracteres.',
  'projects.form.description': 'Descripción (opcional)',
  'projects.form.nameInvalid':
    'El nombre del proyecto debe tener entre {min} y {max} caracteres y no puede estar vacío.',
  'projects.form.createError': 'No se pudo crear el proyecto.',
  'projects.form.cancel': 'Cancelar',
  'projects.form.creating': 'Creando…',
  'projects.form.create': 'Crear',

  // Project detail
  'project.backToProjects': '← Proyectos',
  'project.notSpecified': 'Proyecto no especificado.',
  'project.fallbackTitle': 'Proyecto',
  'project.loadError': 'No se pudo cargar el proyecto: {message}',
  'project.sectionsNav': 'Secciones del proyecto',
  'project.tab.courses': 'Cursos',
  'project.tab.drafts': 'Borradores',
  'project.tab.voiceProfiles': 'Perfiles de voz',
  'project.tab.artStyles': 'Estilos visuales',
  'project.tab.generations': 'Generaciones',
  'project.tab.collaborators': 'Colaboradores',

  // Course statuses
  'courseStatus.draft': 'Borrador',
  'courseStatus.proposed': 'Propuesto',
  'courseStatus.approved': 'Aprobado',
  'courseStatus.generating': 'Generando',
  'courseStatus.ready': 'Listo',

  // Episode generation statuses
  'episodeStatus.none': 'Sin generar',
  'episodeStatus.pending': 'Pendiente',
  'episodeStatus.running': 'En curso',
  'episodeStatus.ready': 'Listo',
  'episodeStatus.failed': 'Fallido',

  // Course detail
  'course.notSpecified': 'Curso no especificado.',
  'course.backToProject': '← Volver al proyecto',
  'course.loading': 'Cargando curso…',
  'course.loadError': 'No se pudo cargar el curso: {message}',
  'course.notFound': 'No se encontró el curso solicitado.',
  'course.language': 'Idioma: {language}',
  'course.episodes': 'Episodios',
  'course.noEpisodes': 'Este curso aún no tiene episodios.',
  'course.episodeIndex': 'Episodio {index}',
  'course.field.script': 'Guion',
  'course.field.scriptAvailable': 'Disponible',
  'course.viewScript': 'Ver guion',
  'course.field.scriptMissing': 'Sin guion',
  'course.field.proposedDuration': 'Duración propuesta',
  'course.field.durationSeconds': '{seconds} s',
  'course.field.durationUndefined': 'No definida',
  'course.field.audio': 'Audio',
  'course.field.video': 'Video',
  'course.generateAudio': 'Generar audio',
  'course.generateVideo': 'Generar video',
  'course.starting': 'Iniciando…',
  'course.audioStarted':
    'Generación de audio iniciada. Sigue el estado en la pestaña Generaciones.',
  'course.videoStarted':
    'Generación de video iniciada. Sigue el estado en la pestaña Generaciones.',
  'course.audioStartError': 'No se pudo iniciar la generación de audio.',
  'course.videoStartError': 'No se pudo iniciar la generación de video.',
  'course.generationQueued': 'Generación de {kind} en cola…',
  'course.generationRunning': 'Generando {kind}…',
  'course.generationReady': '{kind} listo: el reproductor aparecerá abajo.',
  'course.generationFailed': 'La generación de {kind} falló: {message}',
  'course.generationFailedNoDetail': 'inténtalo de nuevo',
  'course.playAudio': 'Reproducir audio',
  'course.playVideo': 'Reproducir video',
  'course.mediaLoading': 'Cargando…',
  'course.mediaUrlError': 'No se pudo obtener el enlace de reproducción.',
  'course.audioUnsupported': 'Tu navegador no admite la reproducción de audio.',
  'course.videoUnsupported': 'Tu navegador no admite la reproducción de video.',

  // Agent generate section
  'agent.title': 'Generar curso con agente',
  'agent.close': 'Cerrar',
  'agent.open': 'Investigar y proponer estructura',
  'agent.lead':
    'El agente investigará el tema y propondrá una estructura de {min} a {max} episodios que podrás revisar y editar antes de aprobarla.',
  'agent.field.title': 'Título',
  'agent.field.titlePlaceholder': 'Curso para gestionar un ataque de pánico',
  'agent.field.titleHint': 'Entre {min} y {max} caracteres.',
  'agent.field.topic': 'Tema del curso',
  'agent.field.topicPlaceholder': 'Haz un curso en español que ayude durante un ataque de pánico.',
  'agent.field.topicHint': 'Entre {min} y {max} caracteres.',
  'agent.field.focusAreas': 'Áreas de enfoque (opcional)',
  'agent.field.focusAreasPlaceholder':
    'Respiración, reestructuración cognitiva, técnicas de anclaje…',
  'agent.field.episodes': 'Episodios ({min}–{max})',
  'agent.field.voiceProfile': 'Perfil de voz',
  'agent.field.artStyle': 'Estilo visual',
  'agent.noVoiceProfiles': 'Sin perfiles de voz',
  'agent.noArtStyles': 'Sin estilos visuales',
  'agent.defaultSuffix': ' (predeterminado)',
  'agent.focusAreasPrefix': 'Áreas de enfoque',
  'agent.titleInvalid':
    'El título del curso debe tener entre {min} y {max} caracteres y no puede estar vacío.',
  'agent.topicInvalid':
    'El tema del curso debe tener entre {min} y {max} caracteres y no puede estar vacío.',
  'agent.episodeCountInvalid': 'El número de episodios debe estar entre {min} y {max}.',
  'agent.startError': 'No se pudo iniciar la generación con el agente.',
  'agent.submitting': 'Enviando…',
  'agent.submit': 'Investigar y proponer',
  'agent.generating':
    'El agente está investigando el tema y preparando una propuesta de estructura. Esto puede tardar unos minutos.',
  'agent.generatingFollow': 'También puedes seguir el estado en la pestaña {link}.',
  'agent.generationsLink': 'Borradores',
  'agent.pollTimeout':
    'La propuesta está tardando más de lo habitual. Puedes seguir su estado en la pestaña Borradores.',
  'agent.generationFailed': 'La generación de la propuesta falló. Inténtalo de nuevo.',
  'agent.cancel': 'Cancelar',
  'agent.proposedStructure': 'Estructura propuesta',
  'agent.overviewLabel': 'Plan del agente',
  'agent.reviewHint':
    'Revisa y edita los episodios. El curso debe tener entre {min} y {max} episodios.',
  'agent.episodeTitlePlaceholder': 'Título del episodio',
  'agent.episodeTitleLabel': 'Título del episodio {index}',
  'agent.episodeDurationLabel': 'Duración del episodio {index} en segundos',
  'agent.removeEpisode': 'Eliminar',
  'agent.removeEpisodeLabel': 'Eliminar episodio {index}',
  'agent.addEpisode': 'Añadir episodio',
  'agent.episodeCount': '{count} de {max} episodios',
  'agent.regenerateError': 'No se pudo solicitar una nueva propuesta.',
  'agent.episodeCountRangeError': 'El curso debe tener entre {min} y {max} episodios.',
  'agent.episodeTitleRequired': 'Cada episodio necesita un título.',
  'agent.episodeDurationRequired':
    'Cada episodio necesita una duración en segundos mayor que cero.',
  'agent.approveError': 'No se pudo aprobar la estructura.',
  'agent.approving': 'Aprobando…',
  'agent.approve': 'Aprobar y generar guiones',
  'agent.requesting': 'Solicitando…',
  'agent.reject': 'Rechazar y regenerar',
  'agent.discard': 'Descartar',
  'agent.approved':
    'Estructura aprobada. Los guiones se están generando; sigue el progreso en la pestaña Generaciones.',
  'agent.generateAnother': 'Generar otro curso',

  // Pestaña Borradores — generaciones de curso con IA en progreso (proponiendo,
  // pendientes de revisión, generando o fallidas) y la vista de detalle.
  'drafts.title': 'Borradores',
  'drafts.refresh': 'Actualizar',
  'drafts.loading': 'Cargando borradores…',
  'drafts.loadError': 'No se pudieron cargar los borradores: {message}',
  'drafts.empty':
    'Aún no hay borradores. Inicia una generación de curso con IA desde la pestaña Cursos para crear uno.',
  'drafts.stage.proposing': 'Proponiendo',
  'drafts.stage.awaitingReview': 'Pendiente de revisión',
  'drafts.stage.generating': 'Generando',
  'drafts.stage.failed': 'Fallido',
  'drafts.detail.title': 'Borrador de curso',
  'drafts.detail.back': '← Volver a borradores',
  'drafts.detail.notSpecified': 'No se especificó ningún borrador.',
  'drafts.detail.loading': 'Cargando el borrador…',
  'drafts.detail.loadingCourse': 'Cargando el curso…',
  'drafts.detail.notFound': 'Este borrador ya no está disponible.',
  'drafts.detail.loadError': 'No se pudo cargar el borrador: {message}',
  'drafts.detail.failedNoDescription': 'La generación falló.',
  'drafts.detail.proposing':
    'El agente está investigando el tema y proponiendo una estructura de curso. Esta vista se actualiza automáticamente.',
  'drafts.detail.generating':
    'El curso fue aprobado. Se están generando los guiones y el contenido; esta vista se actualiza automáticamente.',
  'drafts.detail.generated': 'Toda la generación ha finalizado.',
  'drafts.detail.script': 'Guion',
  'drafts.detail.scriptReady': 'listo',
  'drafts.detail.scriptPending': 'pendiente',
  'drafts.detail.audio': 'Audio',
  'drafts.detail.video': 'Video',
  'drafts.detail.openCourse': 'Abrir curso',
  'drafts.detail.scriptsGenerating':
    'El agente está escribiendo un guion de narración para cada episodio. Al terminar, se crean el curso y sus episodios, y aparecen en la pestaña Cursos.',
  'drafts.detail.stepScripts': 'Escribiendo un guion de narración por episodio…',
  'drafts.detail.stepCourse': 'Creando el curso y sus episodios',
  'drafts.detail.stepMedia': 'El audio y el video se generan después, por episodio, desde el curso',
  'drafts.detail.autoRefresh': 'Esta vista se actualiza automáticamente.',
  'drafts.detail.whatGenerated':
    'Cada episodio tiene un guion de narración generado. Genera el audio y el video por episodio desde el curso.',

  // Courses tab
  'courses.title': 'Cursos',
  'courses.loading': 'Cargando cursos…',
  'courses.loadError': 'No se pudieron cargar los cursos: {message}',
  'courses.retry': 'Reintentar',
  'courses.createManually': 'Crear curso manualmente',
  'courses.close': 'Cerrar',
  'courses.empty': 'Este proyecto aún no tiene cursos.',
  'courses.form.title': 'Título',
  'courses.form.titlePlaceholder': 'Introducción a la meditación',
  'courses.form.titleHint': 'Entre {min} y {max} caracteres.',
  'courses.form.topic': 'Tema del curso',
  'courses.form.topicPlaceholder': 'Describe el tema y el enfoque del curso',
  'courses.form.topicHint': 'Entre {min} y {max} caracteres.',
  'courses.form.titleInvalid':
    'El título del curso debe tener entre {min} y {max} caracteres y no puede estar vacío.',
  'courses.form.topicInvalid':
    'El tema del curso debe tener entre {min} y {max} caracteres y no puede estar vacío.',
  'courses.form.createError': 'No se pudo crear el curso.',
  'courses.form.creating': 'Creando…',
  'courses.form.create': 'Crear curso',

  // Collaborators tab
  'collaborators.title': 'Colaboradores',
  'collaborators.loading': 'Cargando colaboradores…',
  'collaborators.loadError': 'No se pudieron cargar los colaboradores: {message}',
  'collaborators.retry': 'Reintentar',
  'collaborators.empty': 'Este proyecto aún no tiene colaboradores.',
  'collaborators.invite.email': 'Correo',
  'collaborators.invite.emailPlaceholder': 'persona@ejemplo.com',
  'collaborators.invite.role': 'Rol',
  'collaborators.invite.emailRequired': 'Introduce el correo de la persona que quieres invitar.',
  'collaborators.invite.error': 'No se pudo invitar al colaborador.',
  'collaborators.invite.inviting': 'Invitando…',
  'collaborators.invite.invite': 'Invitar',
  'collaborators.role.of': 'Rol de {email}',
  'collaborators.roleChangeError': 'No se pudo cambiar el rol.',
  'collaborators.removeError': 'No se pudo quitar al colaborador.',
  'collaborators.removing': 'Quitando…',
  'collaborators.remove': 'Quitar',
  'collaborators.soleOwnerTitle': 'No se puede quitar al único propietario',
  'collaborators.soleOwnerHint': 'Único propietario: no se puede cambiar su rol ni quitarlo.',

  // Voice profiles tab
  'voiceProfiles.title': 'Perfiles de voz',
  'voiceProfiles.loading': 'Cargando perfiles de voz…',
  'voiceProfiles.loadError': 'No se pudieron cargar los perfiles de voz: {message}',
  'voiceProfiles.retry': 'Reintentar',
  'voiceProfiles.empty': 'Este proyecto aún no tiene perfiles de voz.',
  'voiceProfiles.default': 'Predeterminado',
  'voiceProfiles.field.voice': 'Voz',
  'voiceProfiles.field.language': 'Idioma',
  'voiceProfiles.form.name': 'Nombre',
  'voiceProfiles.form.namePlaceholder': 'Voz narradora',
  'voiceProfiles.form.voiceId': 'Identificador de voz',
  'voiceProfiles.form.voiceIdPlaceholder': 'ElevenLabs voiceId',
  'voiceProfiles.form.language': 'Idioma',
  'voiceProfiles.form.languagePlaceholder': 'es',
  'voiceProfiles.form.voiceSettings': 'Ajustes de voz (JSON opcional)',
  'voiceProfiles.form.pausePattern': 'Patrón de pausas (JSON opcional)',
  'voiceProfiles.form.default': 'Marcar como perfil de voz predeterminado',
  'voiceProfiles.form.limitReached': 'Se alcanzó el máximo de {limit} perfiles de voz.',
  'voiceProfiles.form.limitHint':
    'Se alcanzó el máximo de {limit} perfiles de voz para este proyecto.',
  'voiceProfiles.form.requiredFields': 'Nombre, identificador de voz e idioma son obligatorios.',
  'voiceProfiles.form.voiceSettingsLabel': 'Los ajustes de voz',
  'voiceProfiles.form.pausePatternLabel': 'El patrón de pausas',
  'voiceProfiles.form.jsonInvalid': '{field} debe ser JSON válido.',
  'voiceProfiles.form.jsonNotObject': '{field} debe ser un objeto JSON.',
  'voiceProfiles.form.createError': 'No se pudo crear el perfil de voz.',
  'voiceProfiles.form.creating': 'Creando…',
  'voiceProfiles.form.create': 'Crear perfil de voz',

  // Art styles tab
  'artStyles.title': 'Estilos visuales',
  'artStyles.loading': 'Cargando estilos visuales…',
  'artStyles.loadError': 'No se pudieron cargar los estilos visuales: {message}',
  'artStyles.retry': 'Reintentar',
  'artStyles.empty': 'Este proyecto aún no tiene estilos visuales.',
  'artStyles.default': 'Predeterminado',
  'artStyles.field.style': 'Estilo',
  'artStyles.field.palette': 'Paleta',
  'artStyles.field.aspectRatio': 'Relación de aspecto',
  'artStyles.field.resolution': 'Resolución',
  'artStyles.form.name': 'Nombre',
  'artStyles.form.namePlaceholder': 'Acuarela suave',
  'artStyles.form.style': 'Estilo',
  'artStyles.form.stylePlaceholder': 'watercolor',
  'artStyles.form.aspectRatio': 'Relación de aspecto (opcional)',
  'artStyles.form.resolution': 'Resolución (opcional)',
  'artStyles.form.palette': 'Paleta de colores',
  'artStyles.form.paletteColorLabel': 'Elige un color',
  'artStyles.form.paletteAdd': 'Añadir color',
  'artStyles.form.paletteRemove': 'Quitar {color}',
  'artStyles.form.veoHints': 'Sugerencias de prompt para Veo (opcional)',
  'artStyles.form.negativePrompt': 'Prompt negativo (opcional)',
  'artStyles.form.referenceImages': 'Imágenes de referencia (opcional)',
  'artStyles.form.uploading': 'Subiendo…',
  'artStyles.form.uploadError': 'No se pudo subir la imagen. Inténtalo de nuevo.',
  'artStyles.form.referenceRemove': 'Quitar imagen de referencia',
  'artStyles.form.default': 'Marcar como estilo visual predeterminado',
  'artStyles.form.limitReached': 'Se alcanzó el máximo de {limit} estilos visuales.',
  'artStyles.form.limitHint':
    'Se alcanzó el máximo de {limit} estilos visuales para este proyecto.',
  'artStyles.form.requiredFields': 'Nombre y estilo son obligatorios.',
  'artStyles.form.paletteRequired': 'Añade al menos un color a la paleta.',
  'artStyles.form.createError': 'No se pudo crear el estilo visual.',
  'artStyles.form.creating': 'Creando…',
  'artStyles.form.create': 'Crear estilo visual',

  // Generation statuses
  'generationStatus.PENDING': 'Pendiente',
  'generationStatus.RUNNING': 'En curso',
  'generationStatus.READY': 'Lista',
  'generationStatus.FAILED': 'Fallida',

  // Generation kinds
  'generationKind.AGENT_COURSE': 'Estructura del curso',
  'generationKind.EPISODE_AUDIO': 'Audio del episodio',
  'generationKind.EPISODE_VIDEO': 'Video del episodio',

  // Generations tab
  'generations.title': 'Generaciones',
  'generations.loading': 'Cargando generaciones…',
  'generations.loadError': 'No se pudieron cargar las generaciones: {message}',
  'generations.refresh': 'Actualizar',
  'generations.subscribeError':
    'No se pudieron recibir las actualizaciones de generación: {message}',
  'generations.empty':
    'No hay generaciones activas. El estado de cada trabajo aparecerá aquí en tiempo real en cuanto empiece.',
  'generations.episode': ' · Episodio {index}',
  'generations.failedNoDescription': 'La generación falló sin una descripción del error.',
  // API keys
  'apiKeys.title': 'Claves de API',
  'apiKeys.subtitle':
    'Genera claves para llamar a la API mediante programacion como tu usuario, con permisos por clave.',
  'apiKeys.create': 'Crear clave',
  'apiKeys.creating': 'Creando…',
  'apiKeys.loading': 'Cargando claves de API…',
  'apiKeys.retry': 'Reintentar',
  'apiKeys.cancel': 'Cancelar',
  'apiKeys.loadError': 'No se pudieron cargar las claves de API: {message}',
  'apiKeys.createError': 'No se pudo crear la clave de API.',
  'apiKeys.empty': 'Aun no tienes claves de API.',
  'apiKeys.name': 'Nombre',
  'apiKeys.nameRequired': 'El nombre es obligatorio.',
  'apiKeys.scopesLabel': 'Permisos',
  'apiKeys.scopeRequired': 'Selecciona al menos un permiso.',
  'apiKeys.projectsField': 'Restringir a proyectos (opcional)',
  'apiKeys.projectsPlaceholder': 'id-proyecto-1, id-proyecto-2',
  'apiKeys.projectsHint':
    'Ids de proyecto separados por comas. Dejalo vacio para permitir todos tus proyectos.',
  'apiKeys.projectsLabel': 'Proyectos',
  'apiKeys.allProjects': 'Todos los proyectos',
  'apiKeys.expiresField': 'Expira (opcional)',
  'apiKeys.createdLabel': 'Creada',
  'apiKeys.expiresLabel': 'Expira',
  'apiKeys.lastUsedLabel': 'Ultimo uso',
  'apiKeys.revoke': 'Revocar',
  'apiKeys.revoked': 'Revocada',
  'apiKeys.none': 'Ninguno',
  'apiKeys.revealWarning': 'Copia esta clave ahora. No podras verla de nuevo.',
  'apiKeys.copy': 'Copiar',
  'apiKeys.dismiss': 'Descartar',
  'nav.apiKeys': 'Claves de API',
};

// The available dictionaries keyed by language code.
export const dictionaries: Record<Language, Record<TranslationKey, string>> = {
  en,
  es,
};
